import { storageKey, writeLocalJson, removeLocalKey, readLocalJsonOptional } from '@authe/fe-commons/storage';
import { parseAbi, toEventSelector, http, fallback, defineChain, numberToHex, isAddress, maxUint256, getAddress, parseUnits, recoverMessageAddress, isAddressEqual, verifyMessage as verifyMessage$1, erc721Abi, decodeEventLog, decodeFunctionData, encodeFunctionData, zeroAddress, erc20Abi, erc1155Abi, ethAddress, pad, formatUnits, createPublicClient } from 'viem';
export { getAddress, isAddress, isAddressEqual } from 'viem';
import { ChainFamily, registry, isActiveChain, getChainMetadata, toHexChainId, normalizeAddress, chainSelectOptions, chainDisplayName } from '@authe/fe-commons/chains';
import { WagmiProvider, createConfig, injected, usePublicClient, useWalletClient, useSignMessage, useAccount, useChainId, useChains, useSwitchChain, useConnect, useDisconnect } from 'wagmi';
import { createLogger } from '@authe/fe-commons/logging';
import { blockArg, chainIdToNumber } from '@authe/fe-commons/contracts';
import * as React10 from 'react';
import { TwoKeySigner } from '@authe/fe-commons/signing';
import { verifyMessage } from 'viem/actions';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useChainRegistryVersion, Select, SelectTrigger, SelectValue, cn, SelectContent, SelectItem, ChainAvatar, Badge, AddressDisplay, Button } from '@authe/ui-core';
import { isUnlimitedAllowance } from '@authe/fe-commons/value';

// src/config.ts
var WEB3_PACKAGE = "web3";
var WEB3_SELECTED_CHAIN_KEY = storageKey(WEB3_PACKAGE, "chain");
var WEB3_EVM_CONNECTION_ID = "evm:wagmi";
var WEB3_DEFAULT_CHAIN_SLUG = "eth";
var WEB3_SIGNER_TTL_MS = 15 * 6e4;
var WEB3_SIGNER_ROOT_ID = "authe-web3";
var FLAGS_KEY = storageKey(WEB3_PACKAGE, "disconnected");
function readFlags() {
  return readLocalJsonOptional(FLAGS_KEY) ?? {};
}
function markDisconnected(id) {
  const flags = readFlags();
  if (flags[id]) return;
  flags[id] = true;
  writeLocalJson(FLAGS_KEY, flags);
}
function clearDisconnected(id) {
  const flags = readFlags();
  if (!flags[id]) return;
  delete flags[id];
  if (Object.keys(flags).length === 0) removeLocalKey(FLAGS_KEY);
  else writeLocalJson(FLAGS_KEY, flags);
}
function isDisconnected(id) {
  return readFlags()[id] === true;
}
function resolveRpcUrls(chain, overrides) {
  if (overrides) {
    if (chain.chainIdHex && overrides[chain.chainIdHex]?.length) return overrides[chain.chainIdHex];
    if (overrides[chain.slug]?.length) return overrides[chain.slug];
  }
  return [...chain.rpcUrls ?? []];
}
function rpcTransport(urls) {
  if (urls.length <= 1) return http(urls[0]);
  return fallback(urls.map((url) => http(url)));
}
function toViemChain(chain, options = {}) {
  if (chain.family !== ChainFamily.EVM || !chain.chainIdHex) return null;
  let id;
  try {
    id = Number(BigInt(chain.chainIdHex));
  } catch {
    return null;
  }
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  const urls = options.rpcUrls ?? resolveRpcUrls(chain, options.overrides);
  if (!urls.length) return null;
  return defineChain({
    id,
    name: chain.name,
    nativeCurrency: { name: chain.nativeToken, symbol: chain.nativeToken, decimals: chain.decimals },
    rpcUrls: { default: { http: urls } },
    ...chain.explorerUrl ? { blockExplorers: { default: { name: `${chain.name} Explorer`, url: chain.explorerUrl } } } : {},
    testnet: chain.isTestnet
  });
}
function autheEvmViemChains(options = {}) {
  const wanted = options.slugs ? new Set(options.slugs) : null;
  const out = [];
  for (const chain of registry.all()) {
    if (chain.family !== ChainFamily.EVM) continue;
    if (!isActiveChain(chain)) continue;
    if (!options.includeTestnets && chain.isTestnet) continue;
    if (wanted && !wanted.has(chain.slug)) continue;
    const viem = toViemChain(chain, { overrides: options.overrides });
    if (!viem) continue;
    if (chain.slug === WEB3_DEFAULT_CHAIN_SLUG) out.unshift(viem);
    else out.push(viem);
  }
  return out;
}
var log = createLogger(`${WEB3_PACKAGE}:wagmi`);
function createAutheWagmiConfig(options = {}) {
  const resolved = options.chains ?? autheEvmViemChains({
    includeTestnets: options.includeTestnets,
    overrides: options.rpc,
    slugs: options.slugs
  });
  if (resolved.length === 0) {
    throw new Error(
      "createAutheWagmiConfig: no EVM chains resolved from the registry \u2014 supply `rpc` overrides or explicit `chains`."
    );
  }
  const chains = resolved;
  const transports = Object.fromEntries(
    chains.map((c) => [c.id, options.transports?.[c.id] ?? rpcTransport(c.rpcUrls.default.http)])
  );
  log.debug("creating wagmi config", { chains: chains.map((c) => c.id) });
  return createConfig({
    chains,
    connectors: options.connectors ?? [injected()],
    transports,
    ssr: options.ssr
  });
}

// src/contract/keys.ts
function stableArgsKey(args) {
  function encode(value) {
    if (value === null) return ["null"];
    if (Array.isArray(value)) return ["array", value.map(encode)];
    if (typeof value === "object") {
      return ["object", Object.keys(value).sort().map((key) => [key, encode(value[key])])];
    }
    return [typeof value, String(value)];
  }
  return JSON.stringify(encode(args ?? []));
}
function createRpcPublicClients(rpcOverrides) {
  const clients = /* @__PURE__ */ new Map();
  return (chainId) => {
    const meta = getChainMetadata(chainId);
    if (!meta) throw new Error(`Unknown chain: ${chainId}`);
    const chain = toViemChain(meta, { overrides: rpcOverrides });
    if (!chain) throw new Error(`No EVM RPC endpoint for ${meta.name}.`);
    const key = String(chain.id);
    let client = clients.get(key);
    if (!client) {
      client = createPublicClient({ chain, transport: rpcTransport(chain.rpcUrls.default.http) });
      clients.set(key, client);
    }
    return client;
  };
}

// src/contract/reader.ts
var DEFAULT_LOG_WINDOW = 1000n;
function toError(reason) {
  return reason instanceof Error ? reason : new Error(String(reason));
}
function createRpcContractReader(options = {}) {
  const clientFor = options.clientFor ?? createRpcPublicClients(options.rpcOverrides);
  return {
    id: "rpc",
    async read(ref, req) {
      const client = clientFor(ref.chainId);
      return client.readContract({
        address: ref.address,
        abi: req.abi,
        functionName: req.functionName,
        args: req.args,
        ...blockArg(ref.block)
      });
    },
    async readMany(ref, reqs) {
      if (reqs.length === 0) return [];
      const client = clientFor(ref.chainId);
      const settled = await Promise.allSettled(
        reqs.map(
          (r) => client.readContract({
            address: ref.address,
            abi: r.abi,
            functionName: r.functionName,
            args: r.args,
            ...blockArg(ref.block)
          })
        )
      );
      return settled.map(
        (s) => s.status === "fulfilled" ? { status: "success", data: s.value } : { status: "failure", error: toError(s.reason) }
      );
    },
    async call(ref, data) {
      const client = clientFor(ref.chainId);
      const result = await client.call({ to: ref.address, data, ...blockArg(ref.block) });
      return result.data ?? "0x";
    },
    async getStorageAt(ref, slot) {
      const client = clientFor(ref.chainId);
      return client.getStorageAt({ address: ref.address, slot, ...blockArg(ref.block) });
    },
    async resolveBlockNumber(ref) {
      if (ref.block?.blockNumber != null) return ref.block.blockNumber;
      if (ref.block?.blockTag === "pending") throw new Error("Choose a mined block to inspect proxy storage.");
      const block = await clientFor(ref.chainId).getBlock({ blockTag: ref.block?.blockTag ?? "latest" });
      if (block.number == null) throw new Error("The selected block has not been mined.");
      return block.number;
    },
    async getBytecode(ref) {
      return clientFor(ref.chainId).getBytecode({ address: ref.address, ...blockArg(ref.block) });
    },
    async getLogs(ref, query = {}) {
      const client = clientFor(ref.chainId);
      const window2 = query.lookback ?? options.logWindow ?? DEFAULT_LOG_WINDOW;
      const toBlock = query.toBlock ?? ref.block?.blockNumber ?? ref.block?.blockTag ?? "latest";
      let fromBlock = query.fromBlock;
      if (fromBlock === void 0) {
        const head = typeof toBlock === "bigint" ? toBlock : toBlock === "earliest" ? 0n : toBlock === "latest" || toBlock === "pending" ? await client.getBlockNumber() : (await client.getBlock({ blockTag: toBlock })).number;
        fromBlock = head > window2 ? head - window2 : 0n;
      }
      if (typeof fromBlock === "bigint" && fromBlock < 0n || typeof toBlock === "bigint" && toBlock < 0n) {
        throw new Error("Block heights must be non-negative.");
      }
      if (typeof fromBlock === "bigint" && typeof toBlock === "bigint" && fromBlock > toBlock) {
        throw new Error("From block must not be after To block.");
      }
      if (query.topics?.length) {
        const logs2 = await client.request({ method: "eth_getLogs", params: [{
          address: ref.address,
          fromBlock: typeof fromBlock === "bigint" ? numberToHex(fromBlock) : fromBlock,
          toBlock: typeof toBlock === "bigint" ? numberToHex(toBlock) : toBlock,
          topics: [query.topics]
        }] });
        return logs2.map((log7) => ({
          address: log7.address,
          topics: log7.topics,
          data: log7.data,
          blockNumber: log7.blockNumber == null ? null : BigInt(log7.blockNumber),
          logIndex: log7.logIndex == null ? null : Number(BigInt(log7.logIndex)),
          transactionHash: log7.transactionHash
        }));
      }
      const logs = await client.getLogs({ address: ref.address, fromBlock, toBlock });
      return logs.map((log7) => ({
        address: log7.address,
        topics: log7.topics,
        data: log7.data,
        blockNumber: log7.blockNumber,
        logIndex: log7.logIndex,
        transactionHash: log7.transactionHash
      }));
    }
  };
}

// src/contract/readSession.ts
function createContractReadSession(reader, chainId, blockNumber, signal) {
  const reads = /* @__PURE__ */ new Map();
  return (address2, request) => {
    signal?.throwIfAborted();
    const key = stableArgsKey([address2.toLowerCase(), request.functionName, request.args, request.abi]);
    let result = reads.get(key);
    if (!result) {
      result = reader.read({ chainId, address: address2, block: { blockNumber } }, request, { signal });
      if (reads.size < 512) reads.set(key, result);
    }
    return result.then((value) => {
      signal?.throwIfAborted();
      return value;
    });
  };
}
function blockOverride(selector) {
  if (selector.blockNumber != null) return { blockNumber: selector.blockNumber };
  if (selector.blockTag) return { blockTag: selector.blockTag };
  return {};
}
function useEvmRead(params) {
  const { chainId, blockNumber, blockTag, ready, deps, read, onFailure } = params;
  const client = usePublicClient(chainId != null ? { chainId } : void 0);
  const [nonce, setNonce] = React10.useState(0);
  const enabled = ready && Boolean(client);
  const depsKey = stableArgsKey(deps);
  const request = React10.useMemo(
    () => ({ enabled, client, chainId, blockNumber, blockTag, nonce, depsKey }),
    [enabled, client, chainId, blockNumber, blockTag, nonce, depsKey]
  );
  const [result, setResult] = React10.useState(null);
  React10.useEffect(() => {
    if (!enabled || !client) return void 0;
    let cancelled = false;
    Promise.resolve().then(() => read(client, blockOverride({ blockNumber, blockTag }))).then((result2) => {
      if (cancelled) return;
      setResult({ request, data: result2, error: null });
    }).catch((err) => {
      if (cancelled) return;
      if (onFailure) {
        onFailure.log.warn(onFailure.message, { ...onFailure.context?.(), error: String(err) });
      }
      setResult({ request, error: err instanceof Error ? err : new Error(String(err)) });
    });
    return () => {
      cancelled = true;
    };
  }, [request]);
  const refetch = React10.useCallback(() => setNonce((n) => n + 1), []);
  const current = enabled && result?.request === request ? result : null;
  return { data: current?.data, error: current?.error ?? null, isPending: enabled && !current, refetch };
}
var log2 = createLogger(`${WEB3_PACKAGE}:contract-read`);
function useContractRead(params) {
  const { address: address2, abi, functionName, args, chainId, blockNumber, blockTag, enabled = true } = params;
  const argsKey = stableArgsKey(args);
  return useEvmRead({
    chainId,
    blockNumber,
    blockTag,
    ready: enabled && Boolean(address2 && abi && functionName),
    deps: [address2, functionName, argsKey, stableArgsKey(abi)],
    read: async (client, block) => await client.readContract({
      address: address2,
      abi,
      functionName,
      args,
      ...block
    }),
    onFailure: { log: log2, message: "contract read failed", context: () => ({ address: address2, functionName }) }
  });
}
var log3 = createLogger(`${WEB3_PACKAGE}:contract-reads`);
function useContractReads(params) {
  const { calls, chainId, blockNumber, blockTag, enabled = true } = params;
  const callsKey = React10.useMemo(
    () => stableArgsKey(calls),
    [calls]
  );
  const { data, isPending, refetch } = useEvmRead({
    chainId,
    blockNumber,
    blockTag,
    ready: enabled && calls.length > 0,
    deps: [callsKey],
    read: async (client, block) => {
      const settled = await Promise.allSettled(
        calls.map(
          (c) => client.readContract({
            address: c.address,
            abi: c.abi,
            functionName: c.functionName,
            args: c.args,
            ...block
          })
        )
      );
      return settled.map(
        (s) => s.status === "fulfilled" ? { status: "success", data: s.value } : { status: "failure", error: s.reason instanceof Error ? s.reason : new Error(String(s.reason)) }
      );
    },
    onFailure: { log: log3, message: "batch read failed" }
  });
  return { results: data ?? [], isPending, refetch };
}
var PREVIEW_MAX_AGE_MS = 6e4;
var MAX_PREVIEW_DATA_BYTES = 65536;
function validatePreview(request) {
  const chain = getChainMetadata(request.chainId);
  const id = chain?.chainIdHex ? chainIdToNumber(request.chainId) : void 0;
  if (id == null) throw new Error("Choose a registered EVM network for this transaction.");
  if (!isAddress(request.from) || !isAddress(request.to)) throw new Error("Enter valid EVM addresses, including correct mixed-case checksums.");
  if (!/^0x(?:[0-9a-fA-F]{2})*$/.test(request.data) || request.data.length > 2 + MAX_PREVIEW_DATA_BYTES * 2) {
    throw new Error("Calldata must be even-length hex, up to 64 KiB.");
  }
  if (typeof request.value !== "bigint" || request.value < 0n || request.value > maxUint256) {
    throw new Error("Native value must be a non-negative uint256 amount in base units.");
  }
  return { chainId: `eip155:${id}`, from: getAddress(request.from), to: getAddress(request.to), data: request.data, value: request.value };
}
function preparePreviewRequest(draft) {
  const decimals = getChainMetadata(draft.chainId)?.decimals;
  if (decimals == null) throw new Error("The network's native token decimals are unavailable.");
  const value = draft.nativeValue.trim();
  if (value.length > 335 || !/^\d+(?:\.\d+)?$/.test(value) || (value.split(".")[1]?.length ?? 0) > decimals) {
    throw new Error(`Native value must be non-negative with at most ${decimals} decimals.`);
  }
  return validatePreview({
    chainId: draft.chainId,
    from: draft.from.trim(),
    to: draft.to.trim(),
    data: draft.data.trim(),
    value: parseUnits(value, decimals)
  });
}
var previewRequestKey = (r) => [chainIdToNumber(r.chainId) ?? r.chainId, r.from.toLowerCase(), r.to.toLowerCase(), r.data.toLowerCase(), r.value.toString()].join(":");
function previewCanSend(result, request, now, acceptUnavailable) {
  return Boolean(result && previewRequestKey(result.request) === previewRequestKey(request) && Number.isFinite(result.checkedAt) && now - result.checkedAt >= 0 && now - result.checkedAt < PREVIEW_MAX_AGE_MS && (result.status === "success" || result.status === "unavailable" && acceptUnavailable));
}
function assertReviewedTransaction(request, authorization) {
  authorization.signal?.throwIfAborted();
  validatePreview(request);
  if (!previewCanSend(authorization.preview, request, Date.now(), authorization.acceptUnavailable)) {
    throw new Error("The preview expired, failed or no longer matches this transaction. Preview again before signing.");
  }
}

// src/contract/useContractWrite.ts
var log4 = createLogger(`${WEB3_PACKAGE}:contract-write`);
function writeOutcome(receipt) {
  if (!receipt) return "sent";
  return receipt.status === "reverted" ? "reverted" : "success";
}
function useContractWrite(chainId) {
  const { data: walletClient, error: walletError } = useWalletClient(chainId != null ? { chainId } : void 0);
  const publicClient = usePublicClient(chainId != null ? { chainId } : void 0);
  const [hash, setHash] = React10.useState(null);
  const [receipt, setReceipt] = React10.useState(null);
  const [status, setStatus] = React10.useState("idle");
  const [error, setError] = React10.useState(null);
  const attemptRef = React10.useRef(0);
  const inFlight = React10.useRef(false);
  React10.useEffect(() => () => {
    attemptRef.current += 1;
  }, []);
  const reset = React10.useCallback(() => {
    attemptRef.current += 1;
    setHash(null);
    setReceipt(null);
    setStatus("idle");
    setError(null);
  }, []);
  const write = React10.useCallback(
    async (call, value, expectedAccount, beforeSend) => {
      if (inFlight.current) return null;
      const attempt = ++attemptRef.current;
      const current = () => attempt === attemptRef.current;
      let submittedHash = null;
      setHash(null);
      setReceipt(null);
      setError(null);
      if (!walletClient) {
        const err = walletError ?? new Error("No connected wallet to sign the transaction.");
        setError(err);
        setStatus("error");
        return null;
      }
      inFlight.current = true;
      setStatus("signing");
      try {
        if (beforeSend) await beforeSend(false);
        if (expectedAccount) {
          const [liveChain, accounts] = await Promise.all([walletClient.getChainId(), walletClient.getAddresses()]);
          if (liveChain !== chainId || walletClient.chain?.id !== chainId || walletClient.account.address.toLowerCase() !== expectedAccount.toLowerCase() || accounts[0]?.toLowerCase() !== expectedAccount.toLowerCase()) {
            throw new Error("Wallet account or network changed. Review the transaction again.");
          }
        }
        if (!current()) throw new Error("The transaction review was closed or reset before signing.");
        if (beforeSend) await beforeSend(true);
        if (!current()) throw new Error("The transaction review was closed or reset before signing.");
        const txHash = call.data ? await walletClient.sendTransaction({
          to: call.address,
          data: call.data,
          account: walletClient.account,
          chain: walletClient.chain,
          ...value != null ? { value } : {}
        }) : await walletClient.writeContract({
          address: call.address,
          abi: call.abi,
          functionName: call.functionName,
          args: call.args,
          account: walletClient.account,
          chain: walletClient.chain,
          ...value != null ? { value } : {}
        });
        submittedHash = txHash;
        if (current()) {
          setHash(txHash);
          setStatus("pending");
        }
        if (!publicClient) {
          if (current()) setStatus(writeOutcome(null));
          return txHash;
        }
        let replacementReason;
        const r = await publicClient.waitForTransactionReceipt({
          hash: txHash,
          onReplaced: ({ reason }) => {
            replacementReason = reason;
          }
        });
        submittedHash = r.transactionHash;
        if (!current()) return submittedHash;
        setHash(submittedHash);
        setReceipt(r);
        if (replacementReason === "cancelled" || replacementReason === "replaced") {
          setError(new Error(replacementReason === "cancelled" ? "Transaction was cancelled in the wallet." : "Transaction was replaced by a different transaction; the requested call was not confirmed."));
          setStatus("error");
          return submittedHash;
        }
        if (writeOutcome(r) === "reverted") {
          log4.warn("contract write reverted on chain", {
            address: call.address,
            functionName: call.functionName,
            hash: txHash
          });
          setError(
            new Error(
              `Transaction reverted on chain. ${call.functionName} was mined but the contract rejected it, so nothing changed.`
            )
          );
          setStatus("reverted");
          return submittedHash;
        }
        setStatus(writeOutcome(r));
        return submittedHash;
      } catch (err) {
        log4.warn("contract write failed", { address: call.address, functionName: call.functionName, error: String(err) });
        if (current()) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setStatus("error");
        }
        return submittedHash;
      } finally {
        inFlight.current = false;
      }
    },
    [walletClient, walletError, publicClient, chainId]
  );
  const writeReviewed = React10.useCallback(async (input, authorization, call) => {
    const request = validatePreview(input);
    const beforeSend = async (readyToSend) => {
      assertReviewedTransaction(request, authorization);
      if (chainIdToNumber(request.chainId) !== chainId) throw new Error("The reviewed network differs from the signing network.");
      if (readyToSend) {
        if (!publicClient) throw new Error("The preview's block cannot be verified. Reconnect to this network and preview again.");
        const block = await publicClient.getBlock({ blockNumber: authorization.preview.blockNumber });
        if (block.hash !== authorization.preview.blockHash) throw new Error("The preview's block changed. Preview again before signing.");
      }
      assertReviewedTransaction(request, authorization);
    };
    return write(
      { ...call, address: request.to, abi: call?.abi ?? [], functionName: call?.functionName ?? "reviewed transaction", data: request.data },
      request.value,
      request.from,
      beforeSend
    );
  }, [chainId, publicClient, write]);
  return { write, writeReviewed, hash, receipt, status, error, reset };
}
var log5 = createLogger(`${WEB3_PACKAGE}:storage`);
function useEvmStorageAt(params) {
  const { address: address2, slot, chainId, blockNumber, blockTag, enabled = true } = params;
  return useEvmRead({
    chainId,
    blockNumber,
    blockTag,
    ready: enabled && Boolean(address2 && slot),
    deps: [address2, slot],
    read: async (client, block) => await client.getStorageAt({
      address: address2,
      slot,
      ...block
    }),
    onFailure: { log: log5, message: "storage read failed", context: () => ({ address: address2, slot }) }
  });
}
var log6 = createLogger(`${WEB3_PACKAGE}:signer`);
function useAutheSigner(options = {}) {
  const { rootKeyId = WEB3_SIGNER_ROOT_ID, ttlMs = WEB3_SIGNER_TTL_MS, enabled = true } = options;
  const request = React10.useMemo(() => ({ enabled, rootKeyId, ttlMs }), [enabled, rootKeyId, ttlMs]);
  const [state, setState] = React10.useState(null);
  const current = enabled && state?.request === request ? state : null;
  const signer = current?.signer ?? null;
  const error = current?.error ?? null;
  React10.useEffect(() => {
    if (!enabled) return;
    let active = true;
    void TwoKeySigner.create({ rootKeyId, intermediateTtlMs: ttlMs }).then((created) => {
      if (active) setState({ request, signer: created, error: null });
    }).catch((err) => {
      if (!active) return;
      log6.error("two-key signer creation failed", { error: String(err) });
      setState({ request, signer: null, error: err instanceof Error ? err : new Error(String(err)) });
    });
    return () => {
      active = false;
    };
  }, [enabled, rootKeyId, ttlMs, request]);
  const notReady = React10.useCallback(
    () => Promise.reject(
      error ? new Error(`Authe signer is unavailable: ${error.message}`) : new Error("Authe signer is not ready yet.")
    ),
    [error]
  );
  const signRequest = React10.useCallback(
    (payload) => signer ? signer.signRequest(payload) : notReady(),
    [signer, notReady]
  );
  const exportRootPublicJwk = React10.useCallback(
    () => signer ? signer.exportRootPublicJwk() : notReady(),
    [signer, notReady]
  );
  return { signer, ready: signer !== null, error, signRequest, exportRootPublicJwk };
}
function useEvmSignMessage() {
  const { signMessageAsync, isPending, error } = useSignMessage();
  const { address: account } = useAccount();
  const publicClient = usePublicClient();
  const signMessage = React10.useCallback(
    (message) => signMessageAsync({ message }),
    [signMessageAsync]
  );
  const signAndRecover = React10.useCallback(
    async (message) => {
      const signature = await signMessageAsync({ message });
      const signer = await recoverMessageAddress({ message, signature });
      return {
        signature,
        signer,
        matchesAccount: account ? isAddressEqual(signer, account) : null
      };
    },
    [signMessageAsync, account]
  );
  const signAndVerify = React10.useCallback(
    async (message) => {
      const signature = await signMessageAsync({ message });
      if (!account) {
        return { signature, verdict: { valid: false, method: "unknown", reason: "needs-client" } };
      }
      const verdict = await verifyEvmSignature(account, message, signature, publicClient);
      return { signature, verdict };
    },
    [signMessageAsync, account, publicClient]
  );
  return {
    signMessage,
    signAndRecover,
    signAndVerify,
    isPending,
    error: error ?? null
  };
}
function recoverSigner(message, signature) {
  return recoverMessageAddress({ message, signature });
}
async function verifyEvmSignature(address2, message, signature, client) {
  if (client) {
    const valid2 = await verifyMessage(client, { address: address2, message, signature });
    return { valid: valid2, method: "erc1271" };
  }
  const valid = await verifyMessage$1({ address: address2, message, signature });
  if (valid) return { valid: true, method: "eoa" };
  return { valid: false, method: "unknown", reason: "needs-client" };
}
var ContentContext = React10.createContext(null);
function Content() {
  return React10.useContext(ContentContext);
}
var WalletBoundary = React10.memo(function WalletBoundary2({ config, reconnectOnMount }) {
  return /* @__PURE__ */ jsx(WagmiProvider, { config, reconnectOnMount, children: /* @__PURE__ */ jsx(Content, {}) });
});
function Web3Provider({
  config,
  queryClient,
  reconnectOnMount,
  children
}) {
  const client = React10.useMemo(() => queryClient ?? new QueryClient(), [queryClient]);
  const [resolvedReconnect] = React10.useState(
    () => reconnectOnMount ?? !isDisconnected(WEB3_EVM_CONNECTION_ID)
  );
  return /* @__PURE__ */ jsx(QueryClientProvider, { client, children: /* @__PURE__ */ jsx(ContentContext.Provider, { value: children, children: /* @__PURE__ */ jsx(WalletBoundary, { config, reconnectOnMount: resolvedReconnect }) }) });
}
function persistSlug(slug) {
  if (slug) writeLocalJson(WEB3_SELECTED_CHAIN_KEY, slug);
}
function useSelectedChain() {
  const configuredChainId = useChainId();
  const account = useAccount();
  const chains = useChains();
  const chainId = account.isConnected && account.chainId != null ? account.chainId : configuredChainId;
  const { switchChain, isPending, error } = useSwitchChain();
  const chain = getChainMetadata(String(chainId));
  const requestSwitch = React10.useCallback(
    (id, slug) => {
      switchChain({ chainId: id }, { onSuccess: () => persistSlug(slug) });
    },
    [switchChain]
  );
  const switchToChainId = React10.useCallback(
    (id) => requestSwitch(id, getChainMetadata(String(id))?.slug),
    [requestSwitch]
  );
  const switchToSlug = React10.useCallback(
    (slug) => {
      const hex = toHexChainId(slug);
      if (!hex) return false;
      const id = Number(BigInt(hex));
      if (!Number.isSafeInteger(id) || !chains.some((configured) => configured.id === id)) return false;
      requestSwitch(id, getChainMetadata(slug)?.slug);
      return true;
    },
    [requestSwitch, chains]
  );
  return {
    chainId,
    chain,
    isSupported: chains.some((configured) => configured.id === chainId),
    isSwitching: isPending,
    switchError: error ?? null,
    switchToChainId,
    switchToSlug
  };
}
function useAddressInventory() {
  const { address: address2, addresses, chainId, isConnected, status, connector } = useAccount();
  const entries = React10.useMemo(() => {
    if (!isConnected) return [];
    const list = addresses ?? (address2 ? [address2] : []);
    const activeLower = address2?.toLowerCase();
    return list.map((a) => ({
      address: a,
      normalized: normalizeAddress(a),
      isActive: a.toLowerCase() === activeLower
    }));
  }, [addresses, address2, isConnected]);
  const active = React10.useMemo(() => entries.find((e) => e.isActive) ?? null, [entries]);
  const chain = chainId != null ? getChainMetadata(String(chainId)) : null;
  return { entries, active, chainId, chain, isConnected, status, connector: connector?.name };
}

// src/wallet/connect-recovery.ts
function watchStuckWalletConnect(onStuck) {
  if (typeof document === "undefined" || typeof window === "undefined") return () => void 0;
  let timer;
  const schedule = () => {
    if (timer !== void 0) clearTimeout(timer);
    timer = setTimeout(onStuck, 400);
  };
  const onVisibility = () => {
    if (document.visibilityState === "visible") schedule();
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", schedule);
  return () => {
    if (timer !== void 0) clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", schedule);
  };
}

// src/wallet/connection-error.ts
function walletConnectionError(error, wallet = "your wallet") {
  let cause = error;
  for (let depth = 0; cause && typeof cause === "object" && depth < 8; depth += 1) {
    const rpc = cause;
    if (rpc.code === 4001) return new Error("Connection request rejected. Click Connect to try again.");
    if (rpc.code === -32002) return new Error(`A connection request is already open in ${wallet}. Open the extension to approve or reject it, then click Connect again.`);
    cause = rpc.cause;
  }
  return error instanceof Error ? error : new Error("Connection failed. Please try again.");
}

// src/react/useWalletConnection.ts
function useWalletConnection() {
  const { address: address2, isConnected } = useAccount();
  const { connectAsync, connectors: registered, reset } = useConnect();
  const connectors = React10.useMemo(() => registered.some((connector) => connector.type === "injected" && connector.id !== "injected") ? registered.filter((connector) => connector.id !== "injected") : registered, [registered]);
  const { disconnect } = useDisconnect();
  const [isConnecting, setIsConnecting] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const [selectedConnector, setSelectedConnector] = React10.useState();
  const attemptRef = React10.useRef(0);
  React10.useEffect(() => () => {
    attemptRef.current += 1;
  }, []);
  const clearConnecting = React10.useCallback(() => {
    setIsConnecting(false);
    reset();
  }, [reset]);
  const doConnect = React10.useCallback(
    (connector) => {
      const target = connector ?? (connectors.length === 1 ? connectors[0] : void 0);
      if (!connector && connectors.length > 1) {
        setError(new Error("Choose which wallet to connect."));
        return;
      }
      if (!target) {
        setError(new Error("No wallet connector is available."));
        return;
      }
      setSelectedConnector(target);
      const attempt = ++attemptRef.current;
      reset();
      clearDisconnected(WEB3_EVM_CONNECTION_ID);
      setIsConnecting(true);
      setError(null);
      void (async () => {
        try {
          await connectAsync({ connector: target });
          if (attempt !== attemptRef.current && isDisconnected(WEB3_EVM_CONNECTION_ID)) {
            disconnect({ connector: target });
          }
        } catch (e) {
          if (attempt !== attemptRef.current) return;
          setError(walletConnectionError(e, target.name));
          reset();
        } finally {
          if (attempt === attemptRef.current) setIsConnecting(false);
        }
      })();
    },
    [connectAsync, connectors, reset, disconnect]
  );
  React10.useEffect(() => {
    if (!isConnecting) return;
    return watchStuckWalletConnect(() => {
      if (isConnected) return;
      setError((prev) => prev ?? new Error("Connection cancelled"));
      attemptRef.current += 1;
      clearConnecting();
    });
  }, [isConnecting, isConnected, clearConnecting]);
  const doDisconnect = React10.useCallback(() => {
    attemptRef.current += 1;
    clearConnecting();
    setError(null);
    markDisconnected(WEB3_EVM_CONNECTION_ID);
    disconnect();
  }, [disconnect, clearConnecting]);
  return {
    isConnected,
    address: address2,
    connectors,
    connect: doConnect,
    disconnect: doDisconnect,
    isConnecting,
    error,
    selectedConnector
  };
}

// src/wallet/injected.ts
function injected2() {
  return (typeof window !== "undefined" ? window : {}) ?? {};
}
var getEvmProvider = () => injected2().ethereum;
var getSolanaProvider = () => injected2().solana;
var getBitcoinProvider = () => injected2().unisat;

// src/wallet/adapters.ts
function createInjectedEvmAdapter(options = {}) {
  const getProvider = options.getProvider ?? getEvmProvider;
  const toAccounts = (addrs, chainId) => {
    const chain = chainId ? getChainMetadata(chainId)?.slug ?? chainId : void 0;
    return addrs.map((a) => ({ address: a.toLowerCase(), family: ChainFamily.EVM, chain }));
  };
  return {
    id: options.id ?? "injected-evm",
    name: options.name ?? "Browser wallet",
    family: ChainFamily.EVM,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No EVM wallet found");
      const addrs = await provider.request({ method: "eth_requestAccounts" });
      const chainId = await provider.request({ method: "eth_chainId" }).catch(() => void 0);
      return toAccounts(addrs ?? [], chainId);
    },
    async getAccounts() {
      const provider = getProvider();
      if (!provider) return [];
      const addrs = await provider.request({ method: "eth_accounts" }).catch(() => []);
      const chainId = await provider.request({ method: "eth_chainId" }).catch(() => void 0);
      return toAccounts(addrs ?? [], typeof chainId === "string" ? chainId : void 0);
    },
    async disconnect() {
    },
    subscribe(listener) {
      const provider = getProvider();
      if (!provider?.on || !provider.removeListener) return () => void 0;
      let revision = 0;
      const refresh = async (addrs, chainId) => {
        const request = ++revision;
        const [accounts, chain] = await Promise.all([
          addrs ?? provider.request({ method: "eth_accounts" }).catch(() => []),
          chainId ?? provider.request({ method: "eth_chainId" }).catch(() => void 0)
        ]);
        if (request === revision) listener(toAccounts(accounts, typeof chain === "string" ? chain : void 0));
      };
      const onAccounts = (addrs) => {
        void refresh(addrs);
      };
      const onChain = (chainId) => {
        void refresh(void 0, chainId);
      };
      const onDisconnect = () => {
        revision += 1;
        listener([]);
      };
      provider.on("accountsChanged", onAccounts);
      provider.on("chainChanged", onChain);
      provider.on("disconnect", onDisconnect);
      return () => {
        revision += 1;
        provider.removeListener?.("accountsChanged", onAccounts);
        provider.removeListener?.("chainChanged", onChain);
        provider.removeListener?.("disconnect", onDisconnect);
      };
    }
  };
}
function createSolanaAdapter(options = {}) {
  const getProvider = options.getProvider ?? getSolanaProvider;
  const account = (address2) => ({ address: address2, family: ChainFamily.SOLANA, chain: "solana" });
  return {
    id: options.id ?? "phantom",
    name: options.name ?? "Phantom",
    family: ChainFamily.SOLANA,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No Solana wallet found");
      const res = await provider.connect();
      return [account(res.publicKey.toString())];
    },
    async getAccounts() {
      const pk = getProvider()?.publicKey;
      return pk ? [account(pk.toString())] : [];
    },
    async disconnect() {
      await getProvider()?.disconnect();
    },
    subscribe(listener) {
      const provider = getProvider();
      if (!provider?.on || !provider.removeListener) return () => void 0;
      const onAccount = (key) => listener(key ? [account(key.toString())] : []);
      const onConnect = () => onAccount(provider.publicKey ?? null);
      const onDisconnect = () => listener([]);
      provider.on("accountChanged", onAccount);
      provider.on("connect", onConnect);
      provider.on("disconnect", onDisconnect);
      return () => {
        provider.removeListener?.("accountChanged", onAccount);
        provider.removeListener?.("connect", onConnect);
        provider.removeListener?.("disconnect", onDisconnect);
      };
    }
  };
}
function createBitcoinAdapter(options = {}) {
  const getProvider = options.getProvider ?? getBitcoinProvider;
  const toAccounts = (addrs) => (addrs ?? []).map((a) => ({ address: a, family: ChainFamily.UTXO, chain: "bitcoin" }));
  return {
    id: options.id ?? "unisat",
    name: options.name ?? "Unisat",
    family: ChainFamily.UTXO,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No Bitcoin wallet found");
      return toAccounts(await provider.requestAccounts());
    },
    async getAccounts() {
      const provider = getProvider();
      if (!provider) return [];
      return toAccounts(await provider.getAccounts().catch(() => []));
    },
    async disconnect() {
    },
    subscribe(listener) {
      const provider = getProvider();
      if (!provider?.on || !provider.removeListener) return () => void 0;
      const onAccounts = (addrs) => listener(toAccounts(addrs));
      provider.on("accountsChanged", onAccounts);
      return () => provider.removeListener?.("accountsChanged", onAccounts);
    }
  };
}
function defaultWalletAdapters() {
  return [createInjectedEvmAdapter(), createSolanaAdapter(), createBitcoinAdapter()];
}
var WalletsContext = React10.createContext(null);
function WalletProvider({ adapters, autoConnect = true, children }) {
  const adapterList = React10.useMemo(() => adapters ?? defaultWalletAdapters(), [adapters]);
  const value = useWalletHubState(adapterList, autoConnect);
  return /* @__PURE__ */ jsx(WalletsContext.Provider, { value, children });
}
function useWallets() {
  const ctx = React10.useContext(WalletsContext);
  if (!ctx) throw new Error("useWallets must be used within a <WalletProvider>");
  return ctx;
}
function useWalletHubState(adapters, autoConnect) {
  const [snapshot, setSnapshot] = React10.useState(/* @__PURE__ */ new Map());
  const operations = React10.useRef(/* @__PURE__ */ new Map());
  const watchers = React10.useRef(/* @__PURE__ */ new Map());
  const listening = React10.useRef(/* @__PURE__ */ new Set());
  const liveAdapters = React10.useRef(/* @__PURE__ */ new Map());
  const byId = React10.useMemo(() => new Map(adapters.map((a) => [a.id, a])), [adapters]);
  const resolve = React10.useCallback(
    (target) => {
      const adapter = byId.get(typeof target === "string" ? target : target.id);
      return typeof target === "string" || adapter === target ? adapter : void 0;
    },
    [byId]
  );
  const begin = React10.useCallback((adapter) => {
    watchers.current.get(adapter)?.();
    watchers.current.delete(adapter);
    const operation = {};
    operations.current.set(adapter, operation);
    return () => liveAdapters.current.has(adapter) && operations.current.get(adapter) === operation;
  }, []);
  const update = React10.useCallback((adapter, state) => {
    setSnapshot((prev) => new Map(prev).set(adapter, state));
  }, []);
  const applyAccounts = React10.useCallback((adapter, accounts) => {
    update(adapter, { accounts, status: accounts.length ? "connected" : "disconnected" });
  }, [update]);
  const remove = React10.useCallback((adapter) => {
    liveAdapters.current.get(adapter)?.unsubscribe?.();
    liveAdapters.current.delete(adapter);
    operations.current.delete(adapter);
    listening.current.delete(adapter);
    watchers.current.get(adapter)?.();
    watchers.current.delete(adapter);
  }, []);
  React10.useEffect(() => {
    const live = liveAdapters.current;
    return () => {
      for (const adapter of live.keys()) remove(adapter);
    };
  }, [remove]);
  React10.useEffect(() => {
    const next = new Set(adapters);
    const removed = [...liveAdapters.current.keys()].filter((adapter) => !next.has(adapter));
    for (const adapter of removed) remove(adapter);
    if (removed.length) setSnapshot((prev) => {
      const states = new Map(prev);
      for (const adapter of removed) states.delete(adapter);
      return states;
    });
    for (const adapter of adapters) {
      if (!adapter.isAvailable()) continue;
      let entry = liveAdapters.current.get(adapter);
      if (!entry) {
        entry = { hydrated: false };
        liveAdapters.current.set(adapter, entry);
        entry.unsubscribe = adapter.subscribe?.((accounts) => {
          if (!liveAdapters.current.has(adapter) || !listening.current.has(adapter)) return;
          begin(adapter);
          applyAccounts(adapter, accounts);
        });
      }
      if (autoConnect && !entry.hydrated && !listening.current.has(adapter) && !isDisconnected(adapter.id)) {
        entry.hydrated = true;
        listening.current.add(adapter);
        const current = begin(adapter);
        void adapter.getAccounts().then((accounts) => {
          if (current() && listening.current.has(adapter)) applyAccounts(adapter, accounts);
        }).catch(() => void 0);
      }
    }
  }, [adapters, autoConnect, applyAccounts, begin, remove]);
  const connect = React10.useCallback(async (target) => {
    const adapter = resolve(target);
    if (!adapter || !liveAdapters.current.has(adapter)) return;
    liveAdapters.current.get(adapter).hydrated = true;
    const current = begin(adapter);
    listening.current.add(adapter);
    clearDisconnected(adapter.id);
    update(adapter, { accounts: [], status: "connecting" });
    const stopWatch = watchStuckWalletConnect(() => {
      if (!current()) return;
      begin(adapter);
      listening.current.delete(adapter);
      update(adapter, { accounts: [], status: "disconnected", error: "Connection cancelled" });
    });
    watchers.current.set(adapter, stopWatch);
    try {
      const accounts = await adapter.connect();
      if (current()) applyAccounts(adapter, accounts);
    } catch (error) {
      if (current()) {
        listening.current.delete(adapter);
        update(adapter, { accounts: [], status: "error", error: walletConnectionError(error, adapter.name).message });
      }
    } finally {
      stopWatch();
      if (current()) watchers.current.delete(adapter);
    }
  }, [resolve, begin, update, applyAccounts]);
  const disconnect = React10.useCallback(async (target) => {
    const adapter = resolve(target);
    if (!adapter || !liveAdapters.current.has(adapter)) return;
    const current = begin(adapter);
    listening.current.delete(adapter);
    markDisconnected(adapter.id);
    applyAccounts(adapter, []);
    try {
      await adapter.disconnect();
    } catch (error) {
      if (current()) update(adapter, {
        accounts: [],
        status: "disconnected",
        error: error instanceof Error ? error.message : "Disconnect failed"
      });
    }
  }, [resolve, begin, applyAccounts, update]);
  return React10.useMemo(() => {
    const states = new Map(adapters.map((adapter) => [adapter.id, snapshot.get(adapter)]));
    const accountsByAdapter = {};
    const accountsByFamily = {};
    for (const adapter of adapters) {
      const accounts2 = states.get(adapter.id)?.accounts ?? [];
      accountsByAdapter[adapter.id] = accounts2;
      (accountsByFamily[adapter.family] ??= []).push(...accounts2);
    }
    const accounts = Object.values(accountsByAdapter).flat();
    return {
      adapters,
      available: adapters.filter((adapter) => adapter.isAvailable()),
      accounts,
      accountsByAdapter,
      accountsByFamily,
      status: (id) => states.get(id)?.status ?? "disconnected",
      error: (id) => states.get(id)?.error,
      connect,
      disconnect,
      isConnected: (family) => family ? (accountsByFamily[family]?.length ?? 0) > 0 : accounts.length > 0
    };
  }, [adapters, snapshot, connect, disconnect]);
}
function ChainPicker({
  slugs,
  includeTestnets = false,
  placeholder = "Select chain",
  className
}) {
  const { chain, chainId, switchToSlug, switchError, isSwitching } = useSelectedChain();
  const chains = useChains();
  const errorId = React10.useId();
  const registryVersion = useChainRegistryVersion();
  const options = React10.useMemo(() => {
    const wanted = slugs ? new Set(slugs) : null;
    const configured = new Set(chains.map((item) => getChainMetadata(String(item.id))?.slug));
    const all = chainSelectOptions({ valueKind: "slug", evmOnly: true, includeTestnets }).filter((option) => configured.has(option.slug));
    return wanted ? all.filter((option) => wanted.has(option.slug)) : all;
  }, [slugs, includeTestnets, chains, registryVersion]);
  const selection = options.find((option) => option.slug === chain?.slug);
  const currentName = chain?.name ?? chains.find((configured) => configured.id === chainId)?.name ?? `Unsupported network (${chainId})`;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
    /* @__PURE__ */ jsxs(
      Select,
      {
        value: selection?.slug ?? "",
        onValueChange: (slug) => switchToSlug(slug),
        disabled: isSwitching || options.length === 0,
        children: [
          /* @__PURE__ */ jsx(
            SelectTrigger,
            {
              className: cn("w-[200px]", className),
              "aria-label": "Wallet network",
              "aria-describedby": switchError ? errorId : void 0,
              "aria-invalid": switchError ? true : void 0,
              children: /* @__PURE__ */ jsx(SelectValue, { placeholder: selection ? placeholder : currentName })
            }
          ),
          /* @__PURE__ */ jsx(SelectContent, { children: options.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: option.value, children: /* @__PURE__ */ jsx(ChainAvatar, { chain: option.slug, size: "sm" }) }, option.value)) })
        ]
      }
    ),
    switchError && /* @__PURE__ */ jsxs("p", { id: errorId, role: "alert", className: "max-w-[200px] text-2xs text-destructive", children: [
      "Could not switch chain: ",
      switchError.message
    ] })
  ] });
}
var FAMILY_LABEL = {
  evm: "EVM",
  utxo: "Bitcoin",
  solana: "Solana",
  cosmos: "Cosmos",
  xrpl: "XRP Ledger",
  tron: "TRON",
  ton: "TON",
  near: "NEAR",
  aptos: "Aptos",
  sui: "Sui",
  starknet: "Starknet",
  antelope: "Antelope",
  hypercore: "Hyperliquid",
  corda: "Corda",
  fabric: "Hyperledger Fabric",
  canton: "Canton",
  other: "Other"
};
function WalletMenu({ onlyAvailable = false, className }) {
  const { adapters, accountsByAdapter, status, error, connect, disconnect } = useWallets();
  const rows = onlyAvailable ? adapters.filter((a) => a.isAvailable()) : adapters;
  return /* @__PURE__ */ jsx("div", { className: cn("divide-y rounded-md border", className), children: rows.map((adapter) => {
    const available = adapter.isAvailable();
    const account = accountsByAdapter[adapter.id]?.[0];
    const state = status(adapter.id);
    const err = error(adapter.id);
    const connected = state === "connected" && account;
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 px-3 py-2.5", children: [
      /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: adapter.name }),
          /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs", children: FAMILY_LABEL[adapter.family] })
        ] }),
        connected ? /* @__PURE__ */ jsx("div", { className: "font-mono text-xs text-muted-foreground", children: /* @__PURE__ */ jsx(AddressDisplay, { address: account.address, chain: account.chain }) }) : err ? /* @__PURE__ */ jsx("p", { role: "alert", className: "break-words text-xs text-destructive", children: err }) : !available ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Not installed" }) : null
      ] }),
      connected ? /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", "aria-label": `Disconnect ${adapter.name}`, onClick: () => void disconnect(adapter), children: "Disconnect" }) : /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          "aria-label": `Connect ${adapter.name}`,
          disabled: !available,
          onClick: () => void connect(adapter),
          children: state === "connecting" ? "Connecting\u2026" : "Connect"
        }
      )
    ] }, adapter.id);
  }) });
}
function EvmWalletMenu({ className }) {
  const { connectors, connect, disconnect, isConnected, isConnecting, selectedConnector, error } = useWalletConnection();
  const [detected, setDetected] = React10.useState();
  React10.useEffect(() => {
    let current = true;
    void Promise.all(connectors.map(async (connector) => {
      const available = connector.type !== "injected" || Boolean(await connector.getProvider().catch(() => void 0));
      return available ? connector.uid : void 0;
    })).then((ids) => {
      if (current) setDetected({ connectors, ids: new Set(ids.filter((id) => id !== void 0)) });
    });
    return () => {
      current = false;
    };
  }, [connectors]);
  const ready = detected?.connectors === connectors;
  const wallets = connectors.filter((connector) => detected?.ids.has(connector.uid));
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
    isConnected ? /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", onClick: disconnect, children: "Disconnect EVM wallet" }) : !ready ? /* @__PURE__ */ jsx("p", { role: "status", className: "text-sm text-muted-foreground", children: "Checking installed wallets\u2026" }) : wallets.length ? /* @__PURE__ */ jsx("ul", { className: "divide-y rounded-md border", "aria-label": "EVM wallets", children: wallets.map((connector) => /* @__PURE__ */ jsxs("li", { className: "flex items-center justify-between gap-3 px-3 py-2.5", children: [
      /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: connector.name }),
      /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", "aria-label": `Connect ${connector.name}`, onClick: () => connect(connector), children: isConnecting && selectedConnector?.uid === connector.uid ? "Retry connection" : "Connect" })
    ] }, connector.uid)) }) : /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "No EVM wallet detected. Enable MetaMask or another EVM wallet extension in this browser, then reload this page." }),
    isConnecting && /* @__PURE__ */ jsxs("p", { role: "status", className: "text-sm text-muted-foreground", children: [
      "Open ",
      selectedConnector?.name ?? "your wallet",
      " to approve or reject the connection request."
    ] }),
    error && /* @__PURE__ */ jsx("p", { role: "alert", className: "break-words text-sm text-destructive", children: error.message })
  ] });
}
var PERMISSION_ABI = parseAbi([
  "event Approval(address indexed owner,address indexed spender,uint256 value)",
  "event ApprovalForAll(address indexed owner,address indexed operator,bool approved)",
  "event Approval(address indexed owner,address indexed token,address indexed spender,uint160 amount,uint48 expiration)",
  "event Permit(address indexed owner,address indexed token,address indexed spender,uint160 amount,uint48 expiration,uint48 nonce)",
  "event Lockdown(address indexed owner,address token,address spender)",
  "function approve(address spender,uint256 amount)",
  "function setApprovalForAll(address operator,bool approved)",
  "function approve(address token,address spender,uint160 amount,uint48 expiration)",
  "function permit(address owner,address spender,uint256 value,uint256 deadline,uint8 v,bytes32 r,bytes32 s)",
  "function allowance(address owner,address token,address spender) view returns (uint160 amount,uint48 expiration,uint48 nonce)",
  "function lockdown((address token,address spender)[] approvals)"
]);
var MAX_UINT160 = (1n << 160n) - 1n;
function permissionExpiry(seconds) {
  const date = new Date(seconds * 1e3);
  return Number.isNaN(date.getTime()) ? `Unix timestamp ${seconds}` : date.toISOString();
}
var approvalId = (row) => [row.kind, row.contract.toLowerCase(), row.spender.toLowerCase(), row.token?.toLowerCase() ?? "", row.tokenId?.toString() ?? ""].join(":");
function permissionFromLog(log7) {
  if (!isAddress(log7.address, { strict: false })) return null;
  try {
    const abi = log7.topics.length === 4 && log7.data === "0x" ? erc721Abi : PERMISSION_ABI;
    const parsed = decodeEventLog({ abi, data: log7.data, topics: log7.topics, strict: true });
    const args = parsed.args;
    const owner = args.owner;
    const spender = args.spender ?? args.operator ?? args.approved;
    if (!isAddress(owner, { strict: false }) || !isAddress(spender, { strict: false })) return null;
    const contract = log7.address;
    if (parsed.eventName === "ApprovalForAll") return { contract, owner, spender, kind: "operator", approved: Boolean(args.approved), unlimited: Boolean(args.approved) };
    if (parsed.eventName === "Lockdown") return { contract, owner, spender, token: args.token, kind: "permit2", amount: 0n, unlimited: false };
    if (args.tokenId != null) return { contract, owner, spender, kind: "nft", tokenId: BigInt(String(args.tokenId)), unlimited: false };
    const amount = BigInt(String(args.amount ?? args.value));
    return {
      contract,
      owner,
      spender,
      kind: args.token ? "permit2" : "amount",
      amount,
      token: args.token,
      expiresAt: args.expiration == null ? void 0 : Number(args.expiration),
      unlimited: isUnlimitedAllowance(amount, args.token ? 160 : void 0)
    };
  } catch {
    return null;
  }
}
function candidateFromPermission(p) {
  return {
    contract: p.contract,
    spender: p.spender,
    token: p.token,
    tokenId: p.tokenId,
    kind: p.kind === "amount" ? "erc20" : p.kind === "nft" ? "erc721" : p.kind
  };
}
function requestedPermissions(request) {
  try {
    const { functionName, args } = decodeFunctionData({ abi: PERMISSION_ABI, data: request.data });
    const values = args;
    if (functionName === "lockdown") return values[0].map((pair) => ({
      contract: request.to,
      owner: request.from,
      token: pair.token,
      spender: pair.spender,
      kind: "permit2",
      amount: 0n,
      unlimited: false
    }));
    if (functionName === "setApprovalForAll") return [{ contract: request.to, owner: request.from, spender: values[0], kind: "operator", approved: values[1], unlimited: values[1] }];
    if (functionName === "approve" && values.length === 4) return [{ contract: request.to, owner: request.from, token: values[0], spender: values[1], kind: "permit2", amount: values[2], expiresAt: Number(values[3]), unlimited: isUnlimitedAllowance(values[2], 160) }];
    if (functionName === "approve") return [{ contract: request.to, owner: request.from, spender: values[0], kind: "amount", amount: values[1], unlimited: isUnlimitedAllowance(values[1]) }];
    if (functionName === "permit") return [{ contract: request.to, owner: values[0], spender: values[1], kind: "amount", amount: values[2], unlimited: isUnlimitedAllowance(values[2]) }];
  } catch {
  }
  return [];
}
function revocationRequest(chainId, owner, row) {
  const data = row.kind === "operator" ? encodeFunctionData({ abi: PERMISSION_ABI, functionName: "setApprovalForAll", args: [row.spender, false] }) : row.kind === "permit2" ? encodeFunctionData({ abi: PERMISSION_ABI, functionName: "lockdown", args: [[{ token: row.token, spender: row.spender }]] }) : row.kind === "erc721" ? encodeFunctionData({ abi: erc721Abi, functionName: "approve", args: [zeroAddress, row.tokenId] }) : encodeFunctionData({ abi: erc20Abi, functionName: "approve", args: [row.spender, 0n] });
  return { chainId, from: owner, to: row.contract, data, value: 0n };
}

// src/safety/source.ts
var APPROVAL_SCAN_BLOCKS = 10000n;
var APPROVAL_SCAN_ROWS = 60;
var LOG_CHUNK = 1000n;
var TOPICS = [
  "Approval(address,address,uint256)",
  "ApprovalForAll(address,address,bool)",
  "Approval(address,address,address,uint160,uint48)",
  "Permit(address,address,address,uint160,uint48,uint48)",
  "Lockdown(address,address,address)"
].map(toEventSelector);
function safetyError(error) {
  if (error && typeof error === "object" && "shortMessage" in error && typeof error.shortMessage === "string") return error.shortMessage.slice(0, 400);
  return error instanceof Error ? error.message.slice(0, 400) : "The read could not be completed.";
}
function aborted(signal) {
  signal?.throwIfAborted();
}
function address(value) {
  if (!isAddress(value)) throw new Error("Enter a valid EVM address, including a correct checksum when mixed-case.");
  return getAddress(value);
}
async function checkedClient(client, chainId) {
  if (await client.getChainId() !== chainIdToNumber(chainId)) throw new Error("The RPC returned a different network. No assessment was made.");
}
async function verifyBlock(client, number, hash) {
  if ((await client.getBlock({ blockNumber: number })).hash !== hash) throw new Error("The chain reorganized during this check. Run the assessment again.");
}
async function readApproval(read, owner, row, timestamp) {
  const base = {
    kind: row.kind,
    contract: row.contract,
    spender: row.spender,
    token: row.token,
    tokenId: row.tokenId,
    id: approvalId(row),
    status: "unknown",
    unlimited: false
  };
  const uint = (value) => {
    if (typeof value !== "bigint" || value < 0n || value >= 1n << 256n) throw new Error("The contract returned an invalid unsigned amount.");
    return value;
  };
  try {
    if (row.kind === "operator") {
      const active = await read(row.contract, { abi: erc1155Abi, functionName: "isApprovedForAll", args: [owner, row.spender] });
      if (typeof active !== "boolean") throw new Error("The operator permission could not be established.");
      return { ...base, status: active ? "active" : "inactive", unlimited: active };
    }
    if (row.kind === "erc721") {
      const [heldBy, approved] = await Promise.all([
        read(row.contract, { abi: erc721Abi, functionName: "ownerOf", args: [row.tokenId] }),
        read(row.contract, { abi: erc721Abi, functionName: "getApproved", args: [row.tokenId] })
      ]);
      if (typeof heldBy !== "string" || !isAddress(heldBy) || typeof approved !== "string" || !isAddress(approved)) throw new Error("The NFT ownership or permission could not be established.");
      return { ...base, status: heldBy.toLowerCase() === owner.toLowerCase() && approved.toLowerCase() === row.spender.toLowerCase() && BigInt(approved) !== 0n ? "active" : "inactive" };
    }
    const token = row.token ?? row.contract;
    const approval = row.kind === "permit2" ? await read(row.contract, { abi: PERMISSION_ABI, functionName: "allowance", args: [owner, token, row.spender] }) : [await read(token, { abi: erc20Abi, functionName: "allowance", args: [owner, row.spender] })];
    if (!Array.isArray(approval)) throw new Error("The allowance could not be established.");
    const amount = uint(approval[0]);
    const expiresAt = row.kind === "permit2" ? Number(approval[1]) : void 0;
    if (expiresAt != null && (!Number.isSafeInteger(expiresAt) || expiresAt < 0 || expiresAt >= 2 ** 48)) throw new Error("The permission expiration could not be established.");
    const expired = expiresAt != null && BigInt(expiresAt) < timestamp;
    const meta = await Promise.allSettled([
      read(token, { abi: erc20Abi, functionName: "balanceOf", args: [owner] }).then(uint),
      read(token, { abi: erc20Abi, functionName: "decimals" }),
      read(token, { abi: erc20Abi, functionName: "symbol" }),
      ...row.kind === "permit2" ? [read(token, { abi: erc20Abi, functionName: "allowance", args: [owner, row.contract] }).then(uint)] : []
    ]);
    const value = (i) => meta[i]?.status === "fulfilled" ? meta[i].value : void 0;
    const balance = typeof value(0) === "bigint" ? value(0) : void 0;
    const underlying = row.kind === "permit2" ? value(3) : amount;
    const effective = underlying == null ? void 0 : amount < underlying ? amount : underlying;
    const inactive = amount === 0n || expired || underlying === 0n;
    const decimals = value(1);
    const symbol = value(2);
    return {
      ...base,
      status: inactive ? "inactive" : underlying == null ? "unknown" : "active",
      allowance: amount,
      balance,
      expiresAt,
      underlyingAllowance: row.kind === "permit2" ? underlying : void 0,
      exposed: inactive ? 0n : effective == null || balance == null ? void 0 : balance < effective ? balance : effective,
      unlimited: isUnlimitedAllowance(amount, row.kind === "permit2" ? 160 : void 0),
      decimals: typeof decimals === "number" && Number.isInteger(decimals) && decimals >= 0 && decimals <= 255 ? decimals : void 0,
      symbol: typeof symbol === "string" ? symbol.replaceAll("\0", "").trim().slice(0, 40) || void 0 : void 0,
      error: !inactive && (balance == null || underlying == null) ? "Current balance or underlying token permission could not be read; exposure is unknown." : void 0
    };
  } catch (error) {
    return { ...base, error: safetyError(error) };
  }
}
function createWalletSafetySource(clientFor = createRpcPublicClients(), reader = createRpcContractReader({ clientFor })) {
  return {
    async scan(chainId, rawOwner, options = {}) {
      const owner = address(rawOwner);
      const client = clientFor(chainId);
      aborted(options.signal);
      await checkedClient(client, chainId);
      const head = await client.getBlock({ blockTag: "latest" });
      if (head.number == null || !head.hash) throw new Error("No mined block is available.");
      const toBlock = options.toBlock ?? head.number;
      if (toBlock < 0n || toBlock > head.number) throw new Error("Choose a mined, non-negative history block.");
      const fromBlock = toBlock >= APPROVAL_SCAN_BLOCKS ? toBlock - APPROVAL_SCAN_BLOCKS + 1n : 0n;
      const candidates = /* @__PURE__ */ new Map();
      for (const row of options.candidates ?? []) {
        address(row.contract);
        address(row.spender);
        if (row.token) address(row.token);
        if (row.kind === "permit2" && !row.token) throw new Error("A Permit2 permission needs its token address.");
        if (row.kind === "erc721" && (row.tokenId == null || row.tokenId < 0n)) throw new Error("An NFT permission needs its token ID.");
        candidates.set(approvalId(row), row);
      }
      if (candidates.size > APPROVAL_SCAN_ROWS) throw new Error(`Check at most ${APPROVAL_SCAN_ROWS} permissions at once.`);
      const issues = [];
      let readFrom = toBlock + 1n;
      for (let end = toBlock; options.discover !== false && end >= fromBlock; ) {
        aborted(options.signal);
        const start = end - fromBlock >= LOG_CHUNK ? end - LOG_CHUNK + 1n : fromBlock;
        try {
          const logs = await client.request({ method: "eth_getLogs", params: [{ fromBlock: numberToHex(start), toBlock: numberToHex(end), topics: [TOPICS, pad(owner)] }] });
          if (logs.length > 3e3) throw new Error("This range contains too many approval events. Check specific token/spender pairs.");
          for (const log7 of logs) {
            if (log7.removed || !log7.topics) continue;
            const permission = permissionFromLog({ address: log7.address, data: log7.data, topics: log7.topics });
            if (!permission || permission.owner?.toLowerCase() !== owner.toLowerCase()) continue;
            const row = candidateFromPermission(permission);
            candidates.set(approvalId(row), row);
          }
          if (candidates.size > APPROVAL_SCAN_ROWS) {
            issues.push(`More than ${APPROVAL_SCAN_ROWS} permissions were discovered. Only the first ${APPROVAL_SCAN_ROWS} are assessed; check additional token/spender pairs separately.`);
            break;
          }
          readFrom = start;
        } catch (error) {
          issues.push(`Approval history could not be read for blocks ${start}\u2013${end}: ${safetyError(error)}`);
          break;
        }
        if (start === 0n) break;
        end = start - 1n;
      }
      const read = createContractReadSession(reader, chainId, head.number, options.signal);
      const rows = [];
      for (const row of [...candidates.values()].slice(0, APPROVAL_SCAN_ROWS)) {
        aborted(options.signal);
        rows.push(await readApproval(read, owner, row, head.timestamp));
      }
      aborted(options.signal);
      await verifyBlock(client, head.number, head.hash);
      return {
        owner,
        chainId,
        blockNumber: head.number,
        blockHash: head.hash,
        checkedAt: Date.now(),
        fromBlock: readFrom,
        toBlock,
        nextToBlock: readFrom === 0n ? null : readFrom - 1n,
        rows,
        issues
      };
    },
    async preview(input, signal) {
      const request = validatePreview(input);
      const client = clientFor(request.chainId);
      aborted(signal);
      await checkedClient(client, request.chainId);
      const head = await client.getBlock({ blockTag: "latest" });
      if (head.number == null || !head.hash) throw new Error("No mined block is available.");
      const result = {
        request,
        status: "unavailable",
        blockNumber: head.number,
        blockHash: head.hash,
        checkedAt: Date.now(),
        balanceCoverage: "unavailable",
        balanceChanges: [],
        permissions: [],
        requestedPermissions: requestedPermissions(request),
        limitations: [
          "This is a simulation at a recorded block, not a guarantee of execution or safety. State can change before signing.",
          "Gas fees, non-standard assets, off-chain signatures and permissions not emitted as events may not be included. NFT balance counts do not identify token IDs.",
          "This execution preview does not validate transaction nonce or affordability of network fees. Your wallet must check these before sending.",
          "Revoking an allowance does not cancel existing off-chain permit signatures."
        ]
      };
      const parameters = {
        account: request.from,
        calls: [{ to: request.to, data: request.data, value: request.value }],
        blockNumber: head.number,
        traceTransfers: true,
        validation: false
      };
      try {
        let simulation;
        try {
          simulation = await client.simulateCalls({ ...parameters, traceAssetChanges: true });
          result.balanceCoverage = "measured";
        } catch {
          aborted(signal);
          simulation = await client.simulateCalls(parameters);
          result.limitations.push("Balance changes could not be measured. Only execution and emitted permission events were checked.");
        }
        aborted(signal);
        const call = simulation.results[0];
        if (!call) throw new Error("The node returned no simulation result.");
        if (call.status !== "success" && call.status !== "failure") throw new Error("The node returned an unrecognized simulation status.");
        result.status = call.status === "success" ? "success" : "reverted";
        result.gasUsed = call.gasUsed;
        if (call.status === "failure") result.error = safetyError(call.error);
        else {
          const meta = getChainMetadata(request.chainId);
          result.balanceChanges = simulation.assetChanges.filter((change) => change.value.diff !== 0n).map((change) => ({
            token: change.token.address,
            before: change.value.pre,
            after: change.value.post,
            change: change.value.diff,
            symbol: change.token.address.toLowerCase() === ethAddress.toLowerCase() ? meta?.nativeToken : change.token.symbol?.slice(0, 40),
            decimals: change.token.address.toLowerCase() === ethAddress.toLowerCase() ? meta?.decimals : change.token.decimals
          }));
          result.permissions = (call.logs ?? []).flatMap((log7) => {
            const permission = permissionFromLog({ address: log7.address, data: log7.data, topics: log7.topics });
            return permission ? [permission] : [];
          });
        }
      } catch (error) {
        aborted(signal);
        result.status = "unavailable";
        result.balanceCoverage = "unavailable";
        result.balanceChanges = [];
        result.permissions = [];
        result.error = safetyError(error);
        result.limitations.push("Simulation is unavailable on this RPC. No balance or permission outcome has been verified.");
      }
      aborted(signal);
      await verifyBlock(client, head.number, head.hash);
      return result;
    }
  };
}

// src/safety/useApprovalExposure.ts
var EMPTY = { assessment: null, reading: false, error: null, stale: false };
function useApprovalExposure(source, chainId, owner) {
  const scope = React10.useMemo(() => ({ source, chainId, owner }), [source, chainId, owner]);
  const session = React10.useRef({ scope, assessment: null, controller: null });
  const [view, setView] = React10.useState({ scope, data: EMPTY });
  const state = view.scope === scope ? view.data : EMPTY;
  React10.useEffect(() => () => {
    if (session.current.scope === scope) session.current.controller?.abort();
  }, [scope]);
  const run = React10.useCallback(async (mode, candidates) => {
    if (session.current.scope !== scope) session.current = { scope, assessment: null, controller: null };
    const current = session.current;
    const previous = current.assessment;
    if (mode === "refresh" && !previous) return;
    if (mode === "older" && (!previous?.history || previous.scan.nextToBlock == null)) return;
    current.controller?.abort();
    const attempt = new AbortController();
    current.controller = attempt;
    setView({ scope, data: { assessment: previous, reading: true, error: null, stale: Boolean(previous) } });
    try {
      const discovered = mode === "recent" || mode === "older";
      let scan = await source.scan(chainId, owner, {
        discover: discovered,
        candidates: candidates ?? previous?.scan.rows,
        toBlock: mode === "older" ? previous?.scan.nextToBlock ?? void 0 : void 0,
        signal: attempt.signal
      });
      if (attempt.signal.aborted) return;
      if (scan.chainId !== chainId || scan.owner.toLowerCase() !== owner.toLowerCase()) throw new Error("The assessment returned a different wallet or network.");
      if (mode === "refresh" && previous) {
        scan = {
          ...scan,
          fromBlock: previous.scan.fromBlock,
          toBlock: previous.scan.toBlock,
          nextToBlock: previous.scan.nextToBlock,
          issues: [.../* @__PURE__ */ new Set([...previous.scan.issues, ...scan.issues])]
        };
      } else if (mode === "older" && previous) {
        scan = { ...scan, fromBlock: scan.fromBlock < previous.scan.fromBlock ? scan.fromBlock : previous.scan.fromBlock, toBlock: previous.scan.toBlock };
      }
      const assessment = { scan, history: mode === "refresh" ? previous.history : discovered };
      current.assessment = assessment;
      setView({ scope, data: { assessment, reading: false, error: null, stale: false } });
    } catch (error) {
      if (!attempt.signal.aborted) setView({ scope, data: { assessment: previous, reading: false, error: safetyError(error), stale: Boolean(previous) } });
    }
  }, [source, chainId, owner, scope]);
  const refresh = React10.useCallback(() => run("refresh"), [run]);
  return {
    ...state,
    scan: state.assessment?.scan ?? null,
    history: state.assessment?.history ?? false,
    refresh,
    discover: () => run("recent"),
    older: () => run("older"),
    check: (candidates) => run("pairs", candidates)
  };
}
function SafetyAddress({ address: address2, chainId }) {
  return /* @__PURE__ */ jsx("span", { className: "inline-block max-w-full break-all", title: address2, children: /* @__PURE__ */ jsx(AddressDisplay, { address: address2, chain: chainId, copyable: true }) });
}
function PermissionRow({ permission: p, chainId }) {
  return /* @__PURE__ */ jsxs("li", { className: "space-y-1 rounded border p-3", children: [
    /* @__PURE__ */ jsxs("p", { className: "flex flex-wrap items-center gap-2", children: [
      /* @__PURE__ */ jsx("strong", { children: p.kind === "operator" ? p.approved ? "Collection-wide access" : "Operator access revoked" : p.kind === "nft" ? `NFT #${p.tokenId}` : p.unlimited ? "Unlimited token permission" : `${p.amount ?? 0n} base units permitted` }),
      p.unlimited && /* @__PURE__ */ jsx(Badge, { variant: "destructive", children: "Unlimited permission" })
    ] }),
    /* @__PURE__ */ jsxs("p", { children: [
      "Spender: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: p.spender, chainId })
    ] }),
    /* @__PURE__ */ jsxs("p", { children: [
      "Contract: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: p.contract, chainId })
    ] }),
    p.owner && /* @__PURE__ */ jsxs("p", { children: [
      "Owner: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: p.owner, chainId })
    ] }),
    p.token && /* @__PURE__ */ jsxs("p", { children: [
      "Token: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: p.token, chainId })
    ] }),
    p.expiresAt != null && /* @__PURE__ */ jsxs("p", { children: [
      "Expires: ",
      permissionExpiry(p.expiresAt)
    ] })
  ] });
}
function TransactionPreviewResult({ result }) {
  return /* @__PURE__ */ jsxs("section", { "aria-label": "Transaction preview result", className: "space-y-3 text-sm", children: [
    /* @__PURE__ */ jsx("p", { role: "status", className: "font-semibold", children: result.status === "success" ? result.balanceCoverage === "unavailable" ? "Execution simulated \xB7 balance changes unavailable" : "Simulation completed" : result.status === "reverted" ? "Transaction would revert" : "Simulation unavailable" }),
    /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      "Based on block ",
      result.blockNumber.toString(),
      " \xB7 checked ",
      new Date(result.checkedAt).toISOString(),
      result.gasUsed != null ? ` \xB7 ${result.gasUsed} gas used (price not included)` : ""
    ] }),
    result.error && /* @__PURE__ */ jsx("p", { role: "alert", className: "break-words text-destructive", children: result.error }),
    result.status === "success" && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("h4", { className: "font-medium", children: "Sender balance changes" }),
      result.balanceChanges.length ? /* @__PURE__ */ jsx("ul", { className: "space-y-2", children: result.balanceChanges.map((change, i) => /* @__PURE__ */ jsxs("li", { className: "rounded border p-3", children: [
        /* @__PURE__ */ jsxs("p", { className: "font-medium", children: [
          change.change > 0n ? "+" : "",
          change.decimals == null ? `${change.change} base units` : formatUnits(change.change, change.decimals),
          " ",
          change.symbol ?? "asset"
        ] }),
        /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx(SafetyAddress, { address: change.token, chainId: result.request.chainId }) }),
        /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
          "Before ",
          change.decimals == null ? change.before.toString() : formatUnits(change.before, change.decimals),
          " \u2192 after ",
          change.decimals == null ? change.after.toString() : formatUnits(change.after, change.decimals)
        ] })
      ] }, `${change.token}:${i}`)) }) : /* @__PURE__ */ jsx("p", { children: "No balance changes were returned. This does not establish that every asset was checked." }),
      /* @__PURE__ */ jsx("h4", { className: "font-medium", children: "Permission events emitted by the simulation" }),
      result.permissions.length ? /* @__PURE__ */ jsx("ul", { className: "space-y-2", children: result.permissions.map((p, i) => /* @__PURE__ */ jsx(PermissionRow, { permission: p, chainId: result.request.chainId }, i)) }) : /* @__PURE__ */ jsx("p", { children: "No recognized permission events were returned." })
    ] }),
    result.requestedPermissions.length > 0 && /* @__PURE__ */ jsxs("details", { children: [
      /* @__PURE__ */ jsx("summary", { children: "Requested permissions from calldata" }),
      /* @__PURE__ */ jsx("p", { className: "my-2 text-xs text-muted-foreground", children: "These describe the requested call, not a verified outcome. A matching selector does not prove the contract implements that standard; approve\u2019s numeric argument can also be an NFT ID." }),
      /* @__PURE__ */ jsx("ul", { className: "space-y-2", children: result.requestedPermissions.map((p, i) => /* @__PURE__ */ jsx(PermissionRow, { permission: p, chainId: result.request.chainId }, i)) })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "rounded border bg-muted/30 p-3", children: [
      /* @__PURE__ */ jsx("p", { className: "font-medium", children: "What this preview cannot establish" }),
      /* @__PURE__ */ jsx("ul", { className: "mt-1 list-disc space-y-1 pl-5 text-xs", children: result.limitations.map((note) => /* @__PURE__ */ jsx("li", { children: note }, note)) })
    ] }),
    /* @__PURE__ */ jsxs("details", { className: "text-xs", children: [
      /* @__PURE__ */ jsx("summary", { children: "Block reference" }),
      /* @__PURE__ */ jsx("p", { className: "break-all", children: result.blockHash })
    ] })
  ] });
}
function TransactionReview(props) {
  const source = React10.useMemo(() => props.source ?? createWalletSafetySource(), [props.source]);
  const [scope, setScope] = React10.useState({ source, revision: 0 });
  if (scope.source !== source) setScope({ source, revision: scope.revision + 1 });
  return /* @__PURE__ */ jsx(Review, { ...props, source }, `${previewRequestKey(props.request)}:${scope.revision}`);
}
function Review({ request, source: supplied, onSend, sendDisabledReason, busy = false }) {
  const source = React10.useMemo(() => supplied ?? createWalletSafetySource(), [supplied]);
  const [result, setResult] = React10.useState(null);
  const [reading, setReading] = React10.useState(false);
  const [sending, setSending] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const [acceptUnavailable, setAcceptUnavailable] = React10.useState(false);
  const [now, setNow] = React10.useState(Date.now);
  const controller = React10.useRef(null);
  const sendLock = React10.useRef(false);
  const reviewRegion = React10.useRef(null);
  React10.useEffect(() => {
    reviewRegion.current?.focus();
  }, []);
  React10.useEffect(() => () => controller.current?.abort(), []);
  React10.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1e3);
    return () => clearInterval(timer);
  }, []);
  const preview = async () => {
    controller.current?.abort();
    const attempt = new AbortController();
    controller.current = attempt;
    setReading(true);
    setError(null);
    setResult(null);
    setAcceptUnavailable(false);
    try {
      const next = await source.preview(request, attempt.signal);
      if (!attempt.signal.aborted) {
        setResult(next);
        setNow(Date.now());
      }
    } catch (e) {
      if (!attempt.signal.aborted) setError(safetyError(e));
    } finally {
      if (!attempt.signal.aborted) setReading(false);
    }
  };
  const send = async () => {
    if (!onSend || sendDisabledReason || busy || sendLock.current || !previewCanSend(result, request, Date.now(), acceptUnavailable)) return;
    sendLock.current = true;
    setSending(true);
    setError(null);
    try {
      await onSend(request, { preview: result, acceptUnavailable, signal: controller.current?.signal });
      setResult(null);
      setAcceptUnavailable(false);
    } catch (e) {
      setError(safetyError(e));
    } finally {
      sendLock.current = false;
      setSending(false);
    }
  };
  const meta = getChainMetadata(request.chainId);
  return /* @__PURE__ */ jsxs("section", { ref: reviewRegion, tabIndex: -1, "aria-label": "Review transaction", className: "space-y-3 rounded border p-4 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring", children: [
    /* @__PURE__ */ jsxs("h3", { className: "font-semibold", children: [
      "Review transaction \xB7 ",
      chainDisplayName(request.chainId)
    ] }),
    /* @__PURE__ */ jsxs("p", { children: [
      "From: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: request.from, chainId: request.chainId })
    ] }),
    /* @__PURE__ */ jsxs("p", { children: [
      "To: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: request.to, chainId: request.chainId })
    ] }),
    /* @__PURE__ */ jsxs("p", { children: [
      "Native value: ",
      formatUnits(request.value, meta?.decimals ?? 18),
      " ",
      meta?.nativeToken ?? "native"
    ] }),
    /* @__PURE__ */ jsxs("details", { children: [
      /* @__PURE__ */ jsx("summary", { children: "Exact calldata" }),
      /* @__PURE__ */ jsx("pre", { className: "max-h-40 overflow-auto whitespace-pre-wrap break-all text-xs", children: request.data })
    ] }),
    /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", disabled: reading || busy || sending, onClick: () => void preview(), children: reading ? "Simulating\u2026" : result ? "Refresh preview" : "Run preview" }),
    error && /* @__PURE__ */ jsx("p", { role: "alert", className: "break-words text-destructive", children: error }),
    result && /* @__PURE__ */ jsx(TransactionPreviewResult, { result }),
    result && now - result.checkedAt >= PREVIEW_MAX_AGE_MS && /* @__PURE__ */ jsx("p", { role: "status", children: "This preview has expired. Refresh it before sending." }),
    onSend && /* @__PURE__ */ jsxs("div", { className: "space-y-2 border-t pt-3", children: [
      result?.status === "unavailable" && /* @__PURE__ */ jsxs("label", { className: "flex items-start gap-2 text-xs", children: [
        /* @__PURE__ */ jsx("input", { type: "checkbox", checked: acceptUnavailable, onChange: (e) => setAcceptUnavailable(e.target.checked) }),
        "I understand that the effects could not be simulated and want to review this transaction in my wallet."
      ] }),
      sendDisabledReason && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: sendDisabledReason }),
      /* @__PURE__ */ jsx(Button, { type: "button", disabled: Boolean(sendDisabledReason) || reading || busy || sending || !previewCanSend(result, request, now, acceptUnavailable), onClick: () => void send(), children: busy || sending ? "Waiting for wallet\u2026" : "Review and send in wallet" }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Your wallet asks for the final signature. Previewing does not sign or broadcast anything." })
    ] })
  ] });
}

export { APPROVAL_SCAN_BLOCKS, APPROVAL_SCAN_ROWS, ChainPicker, EvmWalletMenu, MAX_PREVIEW_DATA_BYTES, MAX_UINT160, PERMISSION_ABI, PREVIEW_MAX_AGE_MS, SafetyAddress, TransactionPreviewResult, TransactionReview, WEB3_DEFAULT_CHAIN_SLUG, WEB3_EVM_CONNECTION_ID, WEB3_PACKAGE, WEB3_SELECTED_CHAIN_KEY, WEB3_SIGNER_ROOT_ID, WEB3_SIGNER_TTL_MS, WalletMenu, WalletProvider, Web3Provider, approvalId, assertReviewedTransaction, autheEvmViemChains, blockOverride, candidateFromPermission, clearDisconnected, createAutheWagmiConfig, createBitcoinAdapter, createContractReadSession, createInjectedEvmAdapter, createRpcContractReader, createSolanaAdapter, createWalletSafetySource, defaultWalletAdapters, isDisconnected, markDisconnected, permissionExpiry, permissionFromLog, preparePreviewRequest, previewCanSend, previewRequestKey, readApproval, recoverSigner, requestedPermissions, resolveRpcUrls, revocationRequest, rpcTransport, safetyError, stableArgsKey, toViemChain, useAddressInventory, useApprovalExposure, useAutheSigner, useContractRead, useContractReads, useContractWrite, useEvmRead, useEvmSignMessage, useEvmStorageAt, useSelectedChain, useWalletConnection, useWallets, validatePreview, verifyEvmSignature, writeOutcome };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map